import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * GuidedBIAPage — TurboTax-style progressive BIA entry.
 * Presents one question at a time with AI hints. On completion,
 * posts to /api/bia-engine/processes (same endpoint used by the classic form).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ProgressiveWizard from "../components/ProgressiveWizard";
import { BiaConfigWarningBanner } from "../components/BiaConfigWarningBanner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";

const STEPS = [
  {
    key: "name",
    field: "process_name",
    title: "What business process are you mapping?",
    description: "Give it a short, recognizable name.",
    label: "Process name",
    type: "text",
    placeholder: "e.g., Claims Payment Processing",
    required: true,
    aiHint: "Pick a name your operations team already uses. Avoid acronyms unless well-known.",
  },
  {
    key: "dept",
    field: "department",
    title: "Which department owns it?",
    label: "Department",
    type: "select",
    required: true,
    options: [
      { value: "Finance & Accounting", label: "Finance & Accounting" },
      { value: "Claims Management", label: "Claims Management" },
      { value: "Underwriting", label: "Underwriting" },
      { value: "Information Technology", label: "Information Technology" },
      { value: "Human Resources", label: "Human Resources" },
      { value: "Legal & Compliance", label: "Legal & Compliance" },
      { value: "Customer Experience", label: "Customer Experience" },
      { value: "Operations", label: "Operations" },
    ],
  },
  {
    key: "desc",
    field: "description",
    title: "In one sentence, what does this process do?",
    label: "Description",
    type: "textarea",
    placeholder: "Processes customer claim payments end-to-end from approval to disbursement.",
    required: true,
    aiHint: "Focus on OUTCOMES, not steps. Example: 'Ensures incoming payments reach customer bank accounts within SLA.'",
  },
  {
    key: "mtpd",
    field: "mtpd_hours",
    title: "Maximum Tolerable Period of Disruption (MTPD)?",
    description: "The longest outage this process can survive before causing unacceptable harm.",
    label: "MTPD in hours",
    type: "number",
    min: 1, max: 720, required: true,
    aiHint: "Typical ranges: Critical ops = 2-8h · Important ops = 24-72h · Back-office = 72-168h.",
  },
  {
    key: "rto",
    field: "rto_hours",
    title: "Recovery Time Objective (RTO)?",
    description: "Target time to resume the process after disruption. Must be ≤ MTPD.",
    label: "RTO in hours",
    type: "number",
    min: 1, max: 720, required: true,
    aiHint: (v, form) => form.mtpd_hours
      ? `Your MTPD is ${form.mtpd_hours}h — RTO should be 40-70% of that.`
      : "Typical: Critical = 1-4h · Important = 8-24h.",
  },
  {
    key: "rpo",
    field: "rpo_hours",
    title: "Recovery Point Objective (RPO)?",
    description: "Max acceptable data loss measured in time.",
    label: "RPO in hours",
    type: "number",
    min: 0, max: 168, required: true,
    aiHint: "Financial transactions → 0-1h. Operational data → 4-24h. Reference data → 24-72h.",
  },
  {
    key: "tier",
    field: "criticality_tier",
    title: "What's the business criticality tier?",
    description: "Pick the option that best describes this process.",
    type: "radio",
    required: true,
    options: [
      { value: "Tier 1", label: "Tier 1 — Mission-critical", description: "Outage = immediate regulatory/customer/financial harm" },
      { value: "Tier 2", label: "Tier 2 — Business-critical", description: "Outage causes significant impact within 24 hours" },
      { value: "Tier 3", label: "Tier 3 — Important", description: "Can tolerate outage up to 72 hours" },
      { value: "Tier 4", label: "Tier 4 — Non-critical", description: "Minimal immediate impact" },
    ],
    aiHint: "Regulatory obligations, cash-flow, and customer-facing steps typically = Tier 1.",
  },
];

export default function GuidedBIAPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleComplete = async (values) => {
    setSubmitting(true);
    try {
      // Map wizard fields → BIA API payload
      const payload = {
        process_name: values.process_name,
        department: values.department,
        description: values.description,
        mtpd_hours: Number(values.mtpd_hours),
        rto_hours: Number(values.rto_hours),
        rpo_hours: Number(values.rpo_hours),
        criticality_tier: values.criticality_tier,
        status: "draft",
      };
      const res = await api.post("/bia-engine/processes", payload);
      setDone(res.data);
      toast.success("BIA created!");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to create BIA"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="guided-bia-success">
        <Card className="max-w-xl w-full shadow-lg">
          <CardContent className="p-10 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-bold">BIA Created Successfully</h1>
            <p className="text-slate-600">
              <strong>{done.process_name || "Your process"}</strong> has been saved as a draft.
              Next step: add dependencies, threats, and submit for approval.
            </p>
            <div className="flex gap-3 justify-center pt-4">
              <Button variant="outline" onClick={() => setDone(null)} data-testid="create-another-btn">
                Create another
              </Button>
              <Button onClick={() => navigate("/operational-bia")}
                className="bg-sky-700 hover:bg-sky-800" data-testid="view-bia-list-btn">
                View all BIAs <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="guided-bia-page">
      <BiaConfigWarningBanner />
      <div>
        <div className="flex gap-2 mb-2">
          <Badge className="bg-teal-100 text-teal-700">Guided Mode</Badge>
          <Badge variant="outline">ISO 22301 §8.2.2</Badge>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Create a BIA — Guided</h1>
        <p className="text-slate-600 mt-1">
          One question at a time, with AI hints. Takes 2-3 minutes.{" "}
          <button
            onClick={() => navigate("/operational-bia")}
            className="text-sky-700 hover:underline font-medium"
            data-testid="switch-to-classic-btn"
          >
            Switch to classic form →
          </button>
        </p>
      </div>

      <ProgressiveWizard
        steps={STEPS}
        initialValues={{}}
        onComplete={handleComplete}
        title="Business Impact Analysis"
      />

      {submitting && (
        <p className="text-center text-sm text-slate-500">Creating BIA...</p>
      )}
    </div>
  );
}
